import { IconName } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import parse from "html-react-parser";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import Accordion from "../components/Accordion";
import CustomLink from "../components/CustomLink";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { buildApplicationFormData } from "../helpers/applicationFormData";
import { formatNumber, getStatus } from "../helpers/utils";
import ApplicationPrintMenu from "../components/ApplicationPrintMenu";
import {
  ApplicationProvider,
  useApplicationContext,
} from "../providers/ApplicationProvider";
import { useAuthContext } from "../providers/AuthProvider";
import { useConfigContext } from "../providers/ConfigProvider";

const ApplicationDetails = () => (
  <ApplicationProvider>
    <Main />
  </ApplicationProvider>
);

const Main = () => {
  const { user, logout } = useAuthContext();
  const { reportData, activeReportStatuses } = useConfigContext();

  const {
    application,
    unreadMessages,
    activeReports,
    agreements,
    reports
  } = useApplicationContext();
  const { t } = useTranslation();

  const activeAgreements = agreements?.filter(
    (item) => item.signings.length === 0 ? true : item.signings.filter((s) => s.signer_id === user?.id).length === 0,
  );

  const appType = String(application?.type ?? '');

  // En rapporttype er tilgjengelig for denne søknadstypen når den er aktivert i
  // report_config (enabled_for[appType]). Manglende nøkkel → behandles som
  // aktivert (bakoverkompatibel fallback).
  const isAvailableForAppType = (item: any) => {
      const enabledFor = item?.enabled_for ?? {};
      return !(appType in enabledFor) || enabledFor[appType] === true;
  };
  const categoryForType = (type: string) =>
      reportData?.find((rd) => rd.type === type)?.classification ?? null;

  const rapportActiveCount = (reports ?? []).filter((r) => {
      const cat = categoryForType(r.type);
      return (cat === 'rapport' || !cat) && activeReportStatuses.includes(r.status);
  }).length;

  const anmodningActiveCount = (reports ?? []).filter((r) => {
      const cat = categoryForType(r.type);
      return cat === 'anmodning' && activeReportStatuses.includes(r.status);
  }).length;

  // Fanene vises kun når søknadstypen har minst én aktivert rapporttype av riktig
  // klassifisering (report → Rapporter, request → Forespørsler).
  const hasReportTypes = (reportData ?? []).some(
      (item) => isAvailableForAppType(item) && item?.classification === 'report',
  );
  const hasAnmodningTypes = (reportData ?? []).some(
      (item) => isAvailableForAppType(item) && item?.classification === 'request',
  );

  return (
    <div className="container min-vh-100 d-flex flex-column mt-4 mt-lg-0">
      <Header
        backLink={{ link: "/din-side", text: t("dashboard_your_applications") }}
      />
      <div className="row mb-3 mb-lg-4 mt-4 mt-lg-0">
        <div className="col">
          <div className="fw-medium mb-1">#{application?.id}</div>
          <h2>{application?.name}</h2>
          <h5>{application?.applicant_name}</h5>
        </div>
      </div>
      <div className="tabs row no-print">
        <div className="col">
          <div
            className="nav-wrapper border-bottom border-4 mb-4 mb-lg-5"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <ul className="nav position-relative flex-nowrap">
              <CustomLink to="">{t("dashboard_application_info")}</CustomLink>
              <CustomLink to="filer">{t("dashboard_attachments")}</CustomLink>
              <CustomLink to="meldinger">
                {t("dashboard_messages")}{" "}
                {unreadMessages > 0 && "(" + unreadMessages + ")"}
              </CustomLink>
              {application?.state === 90 && hasReportTypes ? (
                <CustomLink to="rapporter">
                 {t("dashboard_reports")}{" "}
                {rapportActiveCount > 0 && "(" + rapportActiveCount + ")"}
                </CustomLink>
              ) : null}
              {/* Forespørsler-fanen — vises når søknadstypen har en aktivert
                  rapporttype med classification = request */}
              {application?.state === 90 && hasAnmodningTypes ? (
                  <CustomLink to="forespørsler">
                      {t("dashboard_requests")}{" "}
                      {anmodningActiveCount > 0 && "(" + anmodningActiveCount + ")"}
                  </CustomLink>
              ) : null}
              <CustomLink to="del-historien">
                {t("dashboard_share_history")}
              </CustomLink>
              {agreements && agreements.length > 0 ? (
                <CustomLink to="avtaler">
                  {t("dashboard_agreements")}{" "}
                  {activeAgreements && activeAgreements.length > 0
                    ? `(${activeAgreements.length})`
                    : null}
                </CustomLink>
              ) : null}
              {application?.createdby === user?.id ? (
                <CustomLink to="tilgangsstyring">
                  {t("dashboard_sharing_access")}
                </CustomLink>
              ) : null}

              <li
                className="nav-item text-end d-none d-lg-block"
                style={{ flex: 1 }}
              >
                <a
                  className="nav-link d-inline-block px-0"
                  href="#"
                  onClick={logout}
                >
                  {t("logout")}{" "}
                  <FontAwesomeIcon
                    icon={["far", "arrow-right-from-bracket"]}
                    className="ms-1"
                  />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Outlet />
      <Footer />
    </div>
  );
};

export const ApplicationOverview = () => {
  const { application, applicationConfig } = useApplicationContext();
  const { apiCall } = useAuthContext();
  const { companyWebsite } = useConfigContext();
  const { t } = useTranslation();
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [letterData, setLetterData] = useState<any>(null);
  const [isLoadingLetter, setIsLoadingLetter] = useState(false);

  // applicationConfig kommer fra ConfigProvider sin samlede liste (merget mot
  // /rest/applicationtemplate). Vi blokkerer ikke på den — alle felter under
  // rendres fra `application` direkte. Hvis malen ikke er funnet (ny mal som
  // ennå ikke er migrert, eller race ved første mount), faller vi tilbake til
  // type_name fra søknaden.
  if (!application) return false;

  const {
    type_name,
    amount_awarded,
    amount_invested,
    amount_requested,
    state,
    status,
    response,
  } = application;
  const statusObject = getStatus(application?.status ?? 0, t);
  const isUnhandled = state !== 90;

  // Fil-felt: multiple-opplasting (fileupload) beholder ALLE distinkte filer;
  // versjonert enkeltfil (fileuploadsingle) reduseres til nyeste versjon.
  // Sammensetningen ligger i en delt helper, slik at utskrift, PDF og ZIP
  // bygger på nøyaktig samme grunnlag som visningen her.
  const formData = buildApplicationFormData(application, applicationConfig);

  const handleOpenLetterModal = async (e: React.MouseEvent) => {
    e.preventDefault();
    setShowLetterModal(true);
    if (letterData) return;
    setIsLoadingLetter(true);
    try {
      const res = await apiCall({
        action: "getApplicationResponse",
        application_id: application.id,
        response_id: response.id,
      });
      if (res.data.status === 200) {
        setLetterData(res.data.data);
      }
    } finally {
      setIsLoadingLetter(false);
    }
  };
  
  const statusLabel = isUnhandled
    ? t("status_unhandled")
    : (statusObject?.label ?? "");

  return (
    <div className="row gy-4 application-print-area">
      <div className="col-12 order-lg-2 col-lg-4">
        <div className="print-only mb-3 small text-muted">
          {t("application_printed_on")} {format(new Date(), "dd.MM.yyyy")}
        </div>

        <div className="p-3 border rounded">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">{type_name}</h4>
            <span
              className={`${!isUnhandled && statusObject?.className} fw-medium unbreakable`}
            >
              {isUnhandled ? t("status_unhandled") : statusObject?.label}
              <FontAwesomeIcon
                icon={
                  isUnhandled
                    ? ["far", "circle-minus"]
                    : ["far", statusObject?.icon as IconName]
                }
                className="ms-2"
              />
            </span>
          </div>

          {amount_requested ? (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <span>{t("application_amount_requested")}</span>
              <span>Kr. {formatNumber(amount_requested)}</span>
            </div>
          ) : null}

          {!isUnhandled && status === 1 ? (
            <>
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="fw-bold">
                  {t("application_amount_awarded")}
                </span>
                <span className="fw-bold">
                  Kr. {formatNumber(amount_awarded)}
                </span>
              </div>
              {amount_invested && amount_invested > 0 ? (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="fw-bold">
                    {t("application_amount_invested")}
                  </span>
                  <span className="fw-bold">
                    Kr. {formatNumber(amount_invested)}
                  </span>
                </div>
              ) : null}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-muted">
                  {t("application_response_received")}{" "}
                  {response?.senton
                    ? format(new Date(response.senton), "dd.MM.yyyy")
                    : ""}
                </span>
                <a
                  href="#"
                  className="text-primary fw-medium"
                  onClick={handleOpenLetterModal}
                >
                  {t("application_svarbrev")}
                </a>
              </div>
            </>
          ) : null}
        </div>

        <div className="mt-3">
          <ApplicationPrintMenu
            application={application}
            applicationConfig={applicationConfig}
            formData={formData}
            statusLabel={statusLabel}
          />
        </div>
      </div>

      <Modal
        show={showLetterModal}
        onHide={() => setShowLetterModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="px-4 px-lg-5 pb-4">
          {isLoadingLetter ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={["fad", "spinner-third"]} spin size="2x" className="text-primary" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center pb-4 border-bottom">
                <div className="text-muted small mb-1">#{application.id}</div>
                <div className="fw-semibold text-uppercase small text-primary mb-2">
                  {application.applicant_name}
                </div>
                <h4 className="mb-3">{application.name}</h4>
                <div className="d-inline-flex flex-column align-items-center bg-light rounded-3 px-4 py-2">
                  <span className="text-muted small">{t("application_amount_requested")}</span>
                  <span className="fw-bold fs-5">Kr. {formatNumber(amount_requested)}</span>
                </div>
              </div>

              {/* Til / Fra / Emne */}
              <div className="py-3 border-bottom">
                {[
                  { label: "Til", value: letterData?.user_email },
                  { label: "Fra", value: "<ikkesvar@lyfti.no>" },
                  { label: "Emne", value: letterData?.subject },
                ].map(({ label, value }) => (
                  <div key={label} className="d-flex gap-2 mb-1 small">
                    <span className="text-muted fw-semibold" style={{ minWidth: "3.5rem" }}>{label}:</span>
                    <span>{value ?? ""}</span>
                  </div>
                ))}
              </div>

              {/* Letter body */}
              <div className="py-4 border-bottom">
                {letterData?.body ? parse(letterData.body) : null}
              </div>

              {/* Link to company */}
              <div className="d-flex gap-4 pt-3">
                {companyWebsite && (
                  <a href={companyWebsite} target="_blank" rel="noreferrer" className="text-primary text-decoration-none small fw-medium">
                    Gå til hjemmeside
                    <FontAwesomeIcon icon={["fal", "arrow-right"]} className="ms-1" />
                  </a>
                )}
                <a href="/din-side" className="text-primary text-decoration-none small fw-medium">
                  Gå til søknadsportal
                  <FontAwesomeIcon icon={["fal", "arrow-right"]} className="ms-1" />
                </a>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      <div className="col-12 col-lg-8">
        <Accordion formData={formData} application={applicationConfig} />
      </div>
    </div>
  );
};

export default ApplicationDetails;